/**
 * INTEL Panel — rule-based bull/bear scorecard for a single stock.
 *
 * Renders the groups returned by GET /api/india/intel/{isin}:
 * technical, valuation, fundamentals, institutional, shareholder.
 *
 * Visual idea borrowed from BB-Terminal's INTEL screen but in KubAIr's
 * emerald/slate palette. Each signal is a row with a coloured dot,
 * a label and a dim detail string. Grouped under four mini cards plus
 * a verdict header.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Badge } from '../ui/badge';
import { TrendingUp, TrendingDown, Minus, Loader2, AlertCircle } from 'lucide-react';

const INDIA_API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

const LEVEL_META = {
  bull:    { dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', label: 'Bullish' },
  bear:    { dot: 'bg-red-500',     text: 'text-red-600 dark:text-red-400',         label: 'Bearish' },
  neutral: { dot: 'bg-amber-500',   text: 'text-amber-600 dark:text-amber-400',     label: 'Neutral' },
  na:      { dot: 'bg-muted-foreground/40', text: 'text-muted-foreground',          label: 'N/A' },
};

const GROUP_TITLES = {
  technical:     'Technical',
  valuation:     'Valuation',
  fundamentals:  'Fundamentals',
  institutional: 'Institutional',
  shareholder:   'Shareholder',
};

const GROUP_BLURB = {
  technical:     '50d / 200d MA and 52-week range positioning.',
  valuation:     'PE, EV/EBITDA and P/B against bucket thresholds.',
  fundamentals:  'Operating margin, net margin and return on equity.',
  institutional: 'FII / DII QoQ deltas, promoter stability and insider filings.',
  shareholder:   'Dividend yield signal.',
};


function SignalRow({ signal }) {
  const meta = LEVEL_META[signal.level] || LEVEL_META.na;
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-border/30 last:border-0">
      <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${meta.dot}`} />
      <div className="flex-1 min-w-0 flex items-baseline justify-between gap-2">
        <span className={`text-xs font-medium ${signal.level === 'na' ? 'text-muted-foreground' : 'text-foreground'}`}>
          {signal.label}
        </span>
        {signal.detail && (
          <span className={`text-[11px] shrink-0 ${meta.text}`}>
            {signal.detail}
          </span>
        )}
      </div>
    </div>
  );
}


function VerdictHeader({ verdict, company }) {
  if (!verdict) return null;
  const v = verdict.verdict;
  const tone =
    v === 'Bullish' ? { icon: TrendingUp, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30' }
    : v === 'Bearish' ? { icon: TrendingDown, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-500/10 border-red-500/30' }
    : v === 'Mixed' ? { icon: Minus, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30' }
    : { icon: AlertCircle, color: 'text-muted-foreground', bg: 'bg-muted/30 border-border/50' };
  const Icon = tone.icon;

  return (
    <div className={`flex items-center justify-between gap-3 p-4 rounded-lg border ${tone.bg}`}>
      <div className="flex items-center gap-3 min-w-0">
        <Icon className={`w-5 h-5 shrink-0 ${tone.color}`} />
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Verdict</p>
          <p className={`text-lg font-bold ${tone.color}`}>{v}</p>
          <p className="text-[11px] text-muted-foreground">
            Rule-based scorecard · {company || '—'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-600 dark:text-emerald-400">
          {verdict.bull} bull
        </Badge>
        <Badge variant="outline" className="text-[10px] border-red-500/40 text-red-600 dark:text-red-400">
          {verdict.bear} bear
        </Badge>
        <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-600 dark:text-amber-400">
          {verdict.neutral} neutral
        </Badge>
        <Badge variant="outline" className="text-[10px] text-muted-foreground">
          {verdict.na} n/a
        </Badge>
      </div>
    </div>
  );
}


function GroupCard({ name, signals }) {
  return (
    <Card className="bg-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center justify-between">
          <span>{GROUP_TITLES[name] || name}</span>
          <span className="text-[10px] font-normal text-muted-foreground">{signals.length} signals</span>
        </CardTitle>
        <p className="text-[11px] text-muted-foreground">{GROUP_BLURB[name]}</p>
      </CardHeader>
      <CardContent className="pt-0">
        {signals.map((s, i) => <SignalRow key={i} signal={s} />)}
      </CardContent>
    </Card>
  );
}


export default function IntelPanel({ isin, companyName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isin) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${INDIA_API}/intel/${isin}`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.detail || err.message || 'Failed to load INTEL'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isin]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-48 w-full" />)}
        </div>
        <div className="flex items-center justify-center text-xs text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> Computing scorecard…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="bg-red-500/5 border-red-500/30">
        <CardContent className="p-4 text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-4">
      <VerdictHeader verdict={data.verdict} company={data.company_name || companyName} />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(data.groups || {}).map(([name, signals]) => (
          <GroupCard key={name} name={name} signals={signals || []} />
        ))}
      </div>

      <p className="text-[10px] text-muted-foreground">
        Rules are heuristics, not predictions. Each signal fires on a fixed threshold, never on AI guessing.
        {data.as_of && <> · Snapshot {data.as_of}</>}
      </p>
    </div>
  );
}
